import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { routeTree } from './routeTree.gen';
import { applyDocumentLanguage } from './lib/locale.ts';
import { isTransientFailure } from '@wundo/client/api.ts';
import { dropInjectedScriptError } from '@wundo/client/dropInjectedScriptError.ts';
import { reportUnlessExpected } from '@wundo/client/reportUnlessExpected.ts';
import { SENTRY_DSN, SENTRY_IGNORED_ERRORS } from './lib/sentry.ts';
import { reportPageVisit } from './lib/pageVisit.ts';
import './app.css';

/* Before the first paint: the markup ships one fixed `lang`, and it is not everyone's. */
applyDocumentLanguage();

/*
 * Session replay is prod only (stage also builds with MODE 'production') and
 * fits a quota of about 50 replays a month: signed-in users only, since the
 * integration is added after sign-in, and a sample of their sessions.
 */
const isProd = import.meta.env.VITE_ENVIRONMENT === 'prod';

Sentry.init({
  dsn: SENTRY_DSN,
  sendDefaultPii: true,
  release: import.meta.env.VITE_APP_VERSION,
  environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE,
  enabled: import.meta.env.MODE === 'production',
  ignoreErrors: SENTRY_IGNORED_ERRORS,
  beforeSend: dropInjectedScriptError,
  replaysSessionSampleRate: isProd ? 0.05 : 0,
  replaysOnErrorSampleRate: isProd ? 1.0 : 0,
});

/*
 * How long an answer is worth keeping, before anything asks for it again.
 *
 * Zero — the default — means every mount, every route change and every return
 * to the tab re-asks: the person's own record was read eight times in two
 * minutes of ordinary use, by pages that all wanted the same thing. Half a
 * minute is short enough that a change made elsewhere shows up while the user
 * is still looking for it, and long enough that walking around the app costs
 * nothing.
 *
 * Refetching on focus stays on. It is what makes a tab notice that staff
 * started or stopped "Login as" in another one, and the queries that watch for
 * that — the session, and the chat — say `staleTime: 0` for themselves.
 */
const STALE_AFTER_MS = 30_000;

/* Refusals are verdicts: retrying a 4xx only delays the message it carries. */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => isTransientFailure(error) && failureCount < 3,
      staleTime: STALE_AFTER_MS,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
});

/* A tab or filter only changes the search, which keeps the user on the same page. */
router.subscribe('onResolved', ({ pathChanged }) => {
  if (!pathChanged) return;

  const page = router.state.matches.at(-1);
  if (page) reportPageVisit(page.fullPath);
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/*
 * Every route renders inside a router error boundary, and React reports an
 * error a boundary catches only to these root callbacks — never to the global
 * handlers Sentry hooks. Passed here, with the console line React would
 * otherwise own, so a crashed page still reaches Sentry.
 */
createRoot(document.getElementById('root')!, {
  onUncaughtError: reportUnlessExpected(Sentry.reactErrorHandler()),
  onCaughtError: reportUnlessExpected(
    Sentry.reactErrorHandler((error, errorInfo) => {
      console.error('Caught error', error, errorInfo.componentStack);
    }),
  ),
}).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
